import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {AssigneePickerAssignee} from '@github-ui/item-picker-assignee-tsq'
import {type Label, LabelsList} from '@github-ui/labels-list'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {clsx} from 'clsx'
import {type ReactElement, useMemo} from 'react'

import type {
  IssueNode,
  IssueViewerPageLabelEdge,
  IssueViewerPageLabelNode,
  IssueViewerPageLabelsConnection,
} from '../issue-viewer-page-query-types'
import {AssigneesMetadata} from './AssigneesMetadata'
import styles from './IssueMetadata.module.css'
import {MilestoneMetadata} from './MilestoneMetadata'

type IssueMetadataAssignedActor = AssigneePickerAssignee
type IssueMetadataLabelNode = Pick<IssueViewerPageLabelNode, keyof Label>
type IssueMetadataLabelEdge = Omit<IssueViewerPageLabelEdge, 'node'> & {
  readonly node: IssueMetadataLabelNode | null
}
type IssueMetadataLabelsConnection = Omit<IssueViewerPageLabelsConnection, 'edges'> & {
  readonly edges: ReadonlyArray<IssueMetadataLabelEdge | null> | null
}

// RELAY-CLEANUP(HeaderMetadata): widens nullable fields so the still-Relay parent data is assignable.
// Remove when HeaderMetadata leaves Relay.
export type IssueMetadataFragment = RelayCompat<
  Pick<IssueNode, 'milestone'> & {
    readonly labels: IssueMetadataLabelsConnection | null
    readonly assignedActors: {
      readonly nodes: ReadonlyArray<IssueMetadataAssignedActor | null> | null
    } | null
  }
>

export const IssueMetadata = ({metadata}: {metadata: IssueMetadataFragment}) => {
  const assigneesData = useMemo(() => {
    return (
      (metadata.assignedActors?.nodes || []).flatMap(assignee => {
        return assignee ? [assignee] : []
      }) ?? []
    )
  }, [metadata.assignedActors?.nodes])

  const labelsData = useMemo(() => {
    return (
      (metadata.labels?.edges || []).flatMap(label => {
        return label?.node ? [label?.node] : []
      }) ?? []
    )
  }, [metadata.labels?.edges])

  const milestoneData = metadata.milestone ?? null

  // return early if no data is available
  if (!assigneesData.length && !labelsData.length && !milestoneData) {
    return null
  }

  const stickyHeaderEnabled = isFeatureEnabled('issues_sticky_header')

  return (
    <div className={clsx(styles.issueMetadata, stickyHeaderEnabled && styles.stickyHeader)}>
      {assigneesData?.length > 0 && (
        <Metadata title="Assignees">
          <AssigneesMetadata assignees={assigneesData} />
        </Metadata>
      )}
      {labelsData?.length > 0 && (
        <Metadata title="Labels">
          <LabelsList labels={labelsData} />
        </Metadata>
      )}
      {milestoneData && (
        <Metadata title="Milestone">
          <MilestoneMetadata milestone={milestoneData} />
        </Metadata>
      )}
    </div>
  )
}

const Metadata = ({title, children}: {title: string; children: ReactElement}) => (
  <div className={styles.metadata}>
    <div className={styles.metadataTitle}>{title}</div>
    <div className={styles.metadataValue}>{children}</div>
  </div>
)
